import type { RhythmScore } from '@/lib/exercise/rhythmEngine';
import { buildRhythmSummary } from '@/lib/exercise/rhythmSummary';

interface RhythmLiveSummaryProps {
  score: RhythmScore;
  hasRests: boolean;
  hasTies: boolean;
  hasDuration: boolean;
}

/**
 * Resumo por item DURANTE a execução. É compacto e fica no fluxo da página
 * (nunca por cima), então no celular aparece abaixo do teclado e não cobre a
 * pauta nem o teclado. Atualiza a cada evento avaliado, porque lê o placar.
 */
export default function RhythmLiveSummary({ score, hasRests, hasTies, hasDuration }: RhythmLiveSummaryProps) {
  const items = buildRhythmSummary(score, {
    hasRests,
    hasTies,
    measuresDuration: hasDuration,
  }).filter((item) => item.applicable);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-3 md:p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 font-label text-sm font-bold text-foreground-800">
          <i className="ri-live-line w-4 h-4 flex items-center justify-center text-primary-600" />
          Resumo ao vivo
        </span>
        <span className="text-[11px] font-label font-semibold text-foreground-500">
          {score.attempts} toque{score.attempts === 1 ? '' : 's'} avaliado
          {score.attempts === 1 ? '' : 's'}
        </span>
      </div>

      <ul className="mt-3 space-y-1.5">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex items-center justify-between gap-2 rounded-md bg-background-100/70 px-2.5 py-1.5"
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <i className={`${item.icon} w-4 h-4 flex items-center justify-center text-primary-600`} />
              <span className="truncate font-label text-xs font-semibold text-foreground-700">
                {item.label}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap">
              {item.notEvaluated ? (
                <span className="text-[11px] font-label font-semibold text-foreground-500">
                  não avaliada
                </span>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1 font-heading text-sm font-bold text-accent-700">
                    <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center" />
                    {item.correct}
                  </span>
                  <span className="inline-flex items-center gap-1 font-heading text-sm font-bold text-secondary-700">
                    <i className="ri-close-line w-3.5 h-3.5 flex items-center justify-center" />
                    {item.wrong}
                  </span>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>

      <p className="mt-2 text-[10.5px] leading-snug text-foreground-500">
        Nota = altura certa · Ataque = momento · Duração = segurar a nota até perto do fim · Pausa =
        silêncio · Ligadura = não reatacou. A aula só desbloqueia <strong>sem erros</strong> nos itens
        exigidos.
      </p>
    </div>
  );
}